import {
  Box,
  Heading,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  createPrismaQuery,
  createPrismaSort,
  DataTable,
  DataTableAddFilter,
  DataTableFilter,
  DataTableFilters,
  DataTableSearch,
  Loosen,
  useCrud,
} from '@pelicargo/datatable';
import { Show } from '@pelicargo/ui';
import { createColumnHelper } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { HiEllipsisVertical } from 'react-icons/hi2';

import { UserFormDrawer } from '../components/Drawers/UserFormDrawer';
import { trpc } from '../config/trpc';

const userFilters: DataTableFilter[] = [
  {
    name: 'is_verified',
    label: 'Is Verified',
    type: 'boolean',
    inputType: 'radio',
    operator: 'equals',
    value: undefined,
    options: [
      {
        label: 'Verified',
        value: true,
      },
      {
        label: 'Not Verified',
        value: false,
      },
    ],
  },
];

type TableColumns = {
  id: number;
  preferred_name: string;
  full_name: string;
  title: string;
  email: string;
  email_verified_at: Date;
  phone_number: string;
  preferred_account: string;
  action?: any;
};

export const UsersScreen = () => {
  const toast = useToast();
  const userDrawer = useDisclosure();

  const { query, handleQueryChange, filters, setFilters, sorting, setSorting, pagination, setPagination, take, skip } =
    useCrud<typeof userFilters>();
  const [selectedUser, setSelectedUser] = useState<TableColumns | null>(null);

  const { data, refetch } = trpc.searchUsers.useQuery({
    skip,
    take,
    search: query,
    orderBy: createPrismaSort(sorting),
    filters: createPrismaQuery(filters),
  });
  const { mutateAsync: verifyUser } = trpc.verifyUser.useMutation();

  const count = data?.count;

  const tableData = useMemo(() => {
    return data?.users?.map((user) => ({
      id: user.id,
      preferred_name: user.preferred_name,
      full_name: user.full_name,
      title: user.title,
      email: user.email,
      phone_number: user.phone_number,
      preferred_account: user.preferred_account?.name,
      email_verified_at: user.email_verified_at,
    }));
  }, [data]);

  const handleClear = () => {
    setFilters([]);
  };

  const onRowClick = useCallback(
    (row: TableColumns) => {
      setSelectedUser(row);
      userDrawer.onOpen();
    },
    [userDrawer],
  );

  const onCloseCallback = () => {
    setSelectedUser(null);
    refetch();
    userDrawer.onClose();
  };

  const handleVerifyEmail = useCallback(
    (id: number) => async () => {
      try {
        await verifyUser({ id });
        refetch();
        toast({
          title: 'Success',
          description: 'User email verified.',
          status: 'success',
        });
      } catch (error) {
        toast({
          title: 'Something went wrong',
          description: error?.message,
          status: 'error',
        });
      }
    },
    [refetch, toast, verifyUser],
  );

  const columnHelper = createColumnHelper<TableColumns>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        header: 'ID',
        size: 0,
        minSize: 0,
      }),
      columnHelper.accessor('full_name', {
        header: 'Full Name',
        size: Number.MIN_SAFE_INTEGER,
      }),
      columnHelper.accessor('title', {
        header: 'Title',
      }),
      columnHelper.accessor('email', {
        header: 'Email',
      }),
      columnHelper.accessor('email_verified_at', {
        header: 'Verified',
        cell: (cell) => (cell?.getValue() ? 'Yes' : 'No'),
      }),
      columnHelper.accessor('phone_number', {
        header: 'Phone Number',
      }),
      columnHelper.accessor('preferred_account', {
        header: 'Preferred Account',
      }),
      columnHelper.accessor('action', {
        header: '',
        size: 0,
        minSize: 0,
        cell: (cell) => {
          const row = cell.row.original;
          return (
            <Box onClick={(e) => e.stopPropagation()}>
              <Menu size="xs">
                <MenuButton
                  as={IconButton}
                  aria-label="Options"
                  size="sm"
                  icon={<HiEllipsisVertical />}
                  variant="ghost"
                />
                <MenuList>
                  <MenuItem onClick={() => onRowClick(row)}>Update</MenuItem>
                  <MenuItem isDisabled={!!row?.email_verified_at} onClick={handleVerifyEmail(row?.id)}>
                    Verify
                  </MenuItem>
                </MenuList>
              </Menu>
            </Box>
          );
        },
      }),
    ],
    [columnHelper, handleVerifyEmail, onRowClick],
  );

  const handleAddFilter = (filter: Loosen<typeof userFilters>[0]) => () => {
    setFilters((prevFilters) => [...prevFilters, filter]);
  };

  return (
    <Stack spacing="0" h="full">
      <Stack p="4" spacing="4">
        <Stack direction="row" justify="space-between" zIndex="99" w="full">
          <Stack direction="row" align="center" w="full">
            <Heading variant="h3">Users</Heading>
            <DataTableSearch value={query} onChange={handleQueryChange} />
            <DataTableAddFilter
              activeFilters={filters?.map((filter) => filter.name)}
              options={userFilters}
              handleAddFilter={handleAddFilter}
            />
          </Stack>
        </Stack>
        <Show if={filters.length > 0}>
          <DataTableFilters filters={filters} setFilters={setFilters} onClear={handleClear} />
        </Show>
      </Stack>
      <DataTable
        columns={columns}
        data={tableData ?? []}
        dataCount={count ?? 0}
        pagination={pagination}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
        onRowClick={onRowClick}
      />
      <UserFormDrawer isOpen={userDrawer.isOpen} onClose={onCloseCallback} userId={selectedUser?.id} />
    </Stack>
  );
};
